import { ReactElement, useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { useStoreActions } from '~/store/hooks';
import { extractErrorMessage } from '~/utils/error/error';
import api from '~/services/api';
import Breadcrumbs from '~/ui/components/common/Breadcrumbs';
import Loader from '~/ui/components/common/Loader';
import { ACT_TEAM_MANAGEMENT } from '~/ui/constants/paths';
import { IInitialValues } from '~/ui/pages/Clinics/ViewClinic/ActTeam/types';
import Form from '~/ui/pages/ActTeam/reusable/Form';

const AddActTeam = (): ReactElement => {
  const { pathname } = useLocation();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [clinicSelectOptions, setClinicSelectOptions] = useState(null);

  const { showError, showNotify } = useStoreActions(actions => actions.snackbar);

  const onMount = useCallback(async () => {
    try {
      setLoading(true);
      const items = await api.clinic.getAllClinics().then(r => r.data);
      const formattedItems = items.map(item => ({
        label: item.name,
        value: item.id,
        hasCrisisTeam: item.hasCrisisTeam,
      }));
      setClinicSelectOptions(formattedItems);
    } catch (e) {
      showError(extractErrorMessage(e));
    } finally {
      setLoading(false);
    }
  }, [showError]);

  useEffect(() => {
    onMount();
  }, [onMount]);

  const breadcrumbItems = [
    {
      to: ACT_TEAM_MANAGEMENT,
      title: 'Team Management',
    },
    {
      to: pathname,
      title: 'Add New Team',
    },
  ];

  if (loading) return <Loader />;

  const onSubmit = async (vals: IInitialValues) => {
    try {
      setIsCreating(true);
      const { clinicId, name, typeId, capacity, isShelterTeam, isCrisisTeam } = vals;
      await api.actTeam.createClinicActTeam(clinicId, {
        name,
        typeId,
        capacity,
        isShelterTeam,
        isCrisisTeam,
      });
      showNotify({ message: 'Team successfully added.' });
      navigate(ACT_TEAM_MANAGEMENT);
    } catch (e) {
      showError(extractErrorMessage(e));
      setIsCreating(false);
    }
  };

  const leavePageDescription = 'Are you sure you want to discard?';

  return (
    <div>
      <h2>Add New Team</h2>
      <Breadcrumbs itemsForBreadcrumbs={breadcrumbItems} />
      <Form
        clinicSelectOptions={clinicSelectOptions}
        isProcessing={isCreating}
        leavePageDescription={leavePageDescription}
        cancelLinkTo={ACT_TEAM_MANAGEMENT}
        onSubmit={onSubmit}
      />
    </div>
  );
};

export default AddActTeam;
